import { useState, useRef, useEffect, useCallback } from 'react';
import { useDidHide, useDidShow } from '../../common/hooks';
import { calculateTime, formatToText } from './utils';

export interface IUseCountDownProps {
  /**
   * 倒计时时长(ms)
   * @default 0
   */
  time?: number;
  /**
   * 时间格式 （DD-日，HH-时，mm-分，ss-秒）
   * @default HH:mm:ss
   */
  format?: string;
  /**
   * 倒计时结束时的回调
   */
  onEnd?: () => void;
}

const useCountdown = ({ time = 0, format = 'HH:mm:ss', onEnd }: IUseCountDownProps) => {
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const timestampRef = useRef(0);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  const cleanTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const countdown = useCallback(() => {
    const calTime = calculateTime(timestampRef.current);
    setDays(calTime.days);
    setHours(calTime.hours);
    setMinutes(calTime.minutes);
    setSeconds(calTime.seconds);

    timestampRef.current -= 1000;

    if (timestampRef.current < 0) {
      if (onEnd && typeof onEnd === 'function') {
        onEnd();
      }
      cleanTimer();
    }
  }, [onEnd]);

  const startCountdown = useCallback(() => {
    if (!(timestampRef.current > 0)) {
      return;
    }
    cleanTimer();
    countdown();
    timerRef.current = setInterval(() => {
      countdown();
    }, 1000);
  }, [countdown]);

  useDidShow(() => {
    startCountdown();
  });

  useDidHide(() => {
    cleanTimer();
  });

  useEffect(() => {
    timestampRef.current = time;
    startCountdown();

    return () => {
      cleanTimer();
    };
  }, [time, startCountdown]);

  return [{ days, hours, minutes, seconds }, formatToText(days, hours, minutes, seconds, format)] as const;
};

export default useCountdown;
